import { useEffect, useState } from 'react';
import { Heart, Bookmark, Share2, Link as LinkIcon, Twitter, Facebook } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { toast } from '@/hooks/use-toast';
import { getReactionState, toggleReaction } from '@/lib/blogReactions';

export function PostActions({ postId, title }: { postId: string; title: string }) {
  const [state, setState] = useState({ likeCount: 0, bookmarkCount: 0, liked: false, bookmarked: false });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    getReactionState(postId).then((s) => alive && setState(s)).catch(() => {});
    return () => { alive = false; };
  }, [postId]);

  const onToggle = async (kind: 'like' | 'bookmark') => {
    if (busy) return;
    setBusy(true);
    const next = kind === 'like' ? !state.liked : !state.bookmarked;
    setState((p) => ({
      ...p,
      [kind === 'like' ? 'liked' : 'bookmarked']: next,
      [kind === 'like' ? 'likeCount' : 'bookmarkCount']:
        (kind === 'like' ? p.likeCount : p.bookmarkCount) + (next ? 1 : -1),
    }));
    try {
      await toggleReaction(postId, kind, next);
    } catch (e: any) {
      toast({ title: 'Action failed', description: e.message, variant: 'destructive' });
      const fresh = await getReactionState(postId);
      setState(fresh);
    } finally {
      setBusy(false);
    }
  };

  const url = typeof window !== 'undefined' ? window.location.href : '';
  const enc = encodeURIComponent;
  const shareLinks = {
    twitter: `https://twitter.com/intent/tweet?url=${enc(url)}&text=${enc(title)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`,
    whatsapp: `https://wa.me/?text=${enc(`${title} ${url}`)}`,
    telegram: `https://t.me/share/url?url=${enc(url)}&text=${enc(title)}`,
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast({ title: 'Link copied' });
    } catch {
      toast({ title: 'Could not copy', variant: 'destructive' });
    }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title, url }); } catch { /* user cancelled */ }
    } else {
      copyLink();
    }
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <Button
        variant={state.liked ? 'default' : 'outline'}
        size="sm"
        onClick={() => onToggle('like')}
        className="gap-1.5"
      >
        <Heart className={`h-4 w-4 ${state.liked ? 'fill-current' : ''}`} />
        <span>{state.likeCount}</span>
      </Button>
      <Button
        variant={state.bookmarked ? 'default' : 'outline'}
        size="sm"
        onClick={() => onToggle('bookmark')}
        className="gap-1.5"
      >
        <Bookmark className={`h-4 w-4 ${state.bookmarked ? 'fill-current' : ''}`} />
        <span className="hidden sm:inline">{state.bookmarked ? 'Saved' : 'Save'}</span>
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="gap-1.5">
            <Share2 className="h-4 w-4" />
            <span className="hidden sm:inline">Share</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={copyLink}><LinkIcon className="h-4 w-4 mr-2" /> Copy link</DropdownMenuItem>
          <DropdownMenuItem onClick={nativeShare}><Share2 className="h-4 w-4 mr-2" /> Share…</DropdownMenuItem>
          <DropdownMenuItem asChild><a href={shareLinks.twitter} target="_blank" rel="noopener noreferrer"><Twitter className="h-4 w-4 mr-2" /> X / Twitter</a></DropdownMenuItem>
          <DropdownMenuItem asChild><a href={shareLinks.facebook} target="_blank" rel="noopener noreferrer"><Facebook className="h-4 w-4 mr-2" /> Facebook</a></DropdownMenuItem>
          <DropdownMenuItem asChild><a href={shareLinks.whatsapp} target="_blank" rel="noopener noreferrer">WhatsApp</a></DropdownMenuItem>
          <DropdownMenuItem asChild><a href={shareLinks.telegram} target="_blank" rel="noopener noreferrer">Telegram</a></DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
